import { LinePlus } from '@qr-front/icons/line-plus'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { cn } from '@qr-front/ui/utils'
import { useAtom, useAtomValue } from 'jotai'

import { InputButton } from '../InputButton'
import {
	defaultRangeEndTime,
	defaultRangeStartTime,
	openingHoursStateAtom,
	sharedOpeningHoursStateAtom,
	weekDays,
	weekDaysAreSharedAtom,
} from './openingHoursState'
import { TOpeningHoursState } from './openingHoursTypes'
import { OpeningHoursTab, selectedTabAtom } from './TabNavigation'
import { TimePicker } from './TimePicker'
import { useTranslatedWeekDays } from './useTranslatedWeekDays'

export const WeekDays = () => {
	const translatedWeekDays = useTranslatedWeekDays()

	const selectedTab = useAtomValue<OpeningHoursTab>(selectedTabAtom)
	const [weekDaysAreShared, setWeekDaysAreShared] = useAtom<boolean>(
		weekDaysAreSharedAtom,
	)
	const [sharedOpeningHoursData, setSharedOpeningHoursData] =
		useAtom<TOpeningHoursState>(sharedOpeningHoursStateAtom)
	const [openingHoursData, setOpeningHoursData] = useAtom<TOpeningHoursState>(
		openingHoursStateAtom,
	)

	const handleToggle = (day: (typeof weekDays)[number]) => {
		const dayData = openingHoursData[day]
		if (!dayData) return

		if (weekDaysAreShared) {
			const openingHoursState: TOpeningHoursState = { ...openingHoursData }

			weekDays.forEach((weekDay) => {
				if (openingHoursState[weekDay]) {
					openingHoursState[weekDay] = {
						...openingHoursState[weekDay],
						isActive: false,
					}
				}
			})

			openingHoursState[day] = {
				...openingHoursState[day],
				isActive: true,
				dateRanges: dayData.dateRanges,
			}

			const sharedOpeningHoursState: TOpeningHoursState = {
				...sharedOpeningHoursData,
			}

			weekDays.forEach((weekDay) => {
				if (sharedOpeningHoursState[weekDay]) {
					sharedOpeningHoursState[weekDay] = {
						...sharedOpeningHoursState[weekDay],
						isActive: false,
					}
				}
			})

			setOpeningHoursData(openingHoursState)
			setSharedOpeningHoursData(sharedOpeningHoursState)
			setWeekDaysAreShared(false)
			return
		}

		setOpeningHoursData({
			...openingHoursData,
			[day]: {
				...dayData,
				isActive: !dayData.isActive,
			},
		})
	}

	const handleTimeChange = (
		day: (typeof weekDays)[number],
		type: 'start' | 'end',
		value: string,
		index: number,
	) => {
		const dayData = openingHoursData[day]
		if (typeof dayData !== 'object' || !dayData) return

		const updatedDateRanges = dayData.dateRanges.map((range, i) => {
			if (i === index) {
				return {
					...range,
					[type]: value,
				}
			}
			return range
		})

		setOpeningHoursData({
			...openingHoursData,
			[day]: {
				...dayData,
				dateRanges: updatedDateRanges,
			},
		})
	}

	const handleAddTimeRange = (day: (typeof weekDays)[number]) => {
		const dayData = openingHoursData[day]
		if (typeof dayData !== 'object' || !dayData) return

		if (dayData.dateRanges.length >= 2) return

		const updatedDateRanges = [
			...dayData.dateRanges,
			{
				start: defaultRangeStartTime,
				end: defaultRangeEndTime,
			},
		]

		setOpeningHoursData({
			...openingHoursData,
			[day]: {
				...dayData,
				dateRanges: updatedDateRanges,
			},
		})
	}

	const handleRemoveTimeRange = (
		day: (typeof weekDays)[number],
		index: number,
	) => {
		const dayData = openingHoursData[day]
		if (typeof dayData !== 'object' || !dayData) return

		if (dayData.dateRanges.length <= 1) return

		const updatedDateRanges = dayData.dateRanges.filter((_, i) => i !== index)

		setOpeningHoursData({
			...openingHoursData,
			[day]: {
				...dayData,
				dateRanges: updatedDateRanges,
			},
		})
	}

	return (
		<>
			{translatedWeekDays?.map((day) => {
				const dayLabel = day.label

				const dayData = openingHoursData[day.key]

				if (!dayData) {
					return null
				}

				const { isActive, dateRanges } = dayData

				return (
					<div key={day.key} className="rounded-10 bg-light-grey-70 p-4">
						<div
							className={cn(
								'flex flex-col gap-4 tablet:flex-row tablet:justify-between',
								!isActive || weekDaysAreShared ? 'opacity-50' : null,
							)}
						>
							<div className="flex gap-2">
								<Checkbox
									id={day.key}
									checked={isActive && !weekDaysAreShared}
									onCheckedChange={() => handleToggle(day.key)}
									label={dayLabel}
									className="type-body-medium mt-2 items-start"
								/>
							</div>

							<div className="flex flex-col gap-2">
								<div className="flex gap-2">
									{dateRanges[0] && (
										<>
											<TimePicker
												value={dateRanges[0].start || defaultRangeStartTime}
												selectedTab={selectedTab}
												onChange={(newStartTime) =>
													handleTimeChange(day.key, 'start', newStartTime, 0)
												}
												disabled={!isActive || weekDaysAreShared}
											/>

											<TimePicker
												value={dateRanges[0].end || defaultRangeEndTime}
												selectedTab={selectedTab}
												onChange={(newEndTime) =>
													handleTimeChange(day.key, 'end', newEndTime, 0)
												}
												disabled={!isActive || weekDaysAreShared}
											/>
										</>
									)}

									<InputButton
										icon={<LinePlus className="size-4" />}
										onClick={() => handleAddTimeRange(day.key)}
										size="icon"
										className={cn(
											!isActive || weekDaysAreShared || dateRanges.length >= 2
												? 'pointer-events-none text-grey'
												: 'text-black',
											isActive && !weekDaysAreShared && dateRanges.length >= 2
												? 'opacity-50'
												: null,
										)}
									/>
								</div>

								{dateRanges.length === 2 && (
									<div className="flex items-center gap-2">
										<TimePicker
											value={dateRanges[1]?.start || defaultRangeStartTime}
											selectedTab={selectedTab}
											onChange={(newStartTime) =>
												handleTimeChange(day.key, 'start', newStartTime, 1)
											}
											disabled={!isActive || weekDaysAreShared}
										/>
										<TimePicker
											value={dateRanges[1]?.end || defaultRangeEndTime}
											selectedTab={selectedTab}
											onChange={(newEndTime) =>
												handleTimeChange(day.key, 'end', newEndTime, 1)
											}
											disabled={!isActive || weekDaysAreShared}
										/>

										<InputButton
											icon={
												<LineTrashAlt
													className={cn(
														'size-4',
														isActive && !weekDaysAreShared
															? 'text-black'
															: 'text-grey',
													)}
												/>
											}
											onClick={() => handleRemoveTimeRange(day.key, 1)}
											size="icon"
											className={`${!isActive || weekDaysAreShared ? 'pointer-events-none' : null}`}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)
			})}
		</>
	)
}
